<template>
    <div
        class="q-pb-lg"
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="isEdit ? update() : create()" ref="eventForm">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :rules="[val => val !== '' || $t('Enter a name')]"
                :label="$t('Name')"
                class="q-mb-md"
                bg-color="white"
                clearable
                outlined
                lazy-rules
                color="accent"
                data-test="name"
            />
            <q-input
                v-model="form.code"
                @clear="form.code = ''"
                :rules="[val => val !== '' || $t('Enter a code')]"
                :label="$t('Code')"
                class="q-mb-md"
                bg-color="white"
                outlined
                clearable
                color="accent"
                data-test="code"
            />
            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                :label="$t('Description')"
                class="q-mb-lg"
                bg-color="white"
                outlined
                clearable
                color="accent"
                data-test="description"
            />

            <div class="form__actions">
                <div>
                    <q-btn
                        v-if="isEdit"
                        @click="handleRemove"
                        :color="
                            $userCannot('delete', 'quality_defects')
                                ? 'grey'
                                : 'negative'
                        "
                        :disable="$userCannot('delete', 'quality_defects')"
                        class="float-left q-px-xs"
                        icon="fas fa-trash-alt"
                    ></q-btn>
                    <q-tooltip v-if="$userCannot('delete', 'quality_defects')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <div>
                    <q-btn
                        :label="$t('Save')"
                        :color="
                            $userCannot('create', 'quality_defects')
                                ? 'grey'
                                : 'primary'
                        "
                        :disable="$userCannot('create', 'quality_defects')"
                        type="submit"
                        class="float-right"
                        id="save-btn"
                    />
                    <q-tooltip v-if="$userCannot('create', 'quality_defects')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    text-color="primary"
                    color="white"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapState, mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'QualityDefectDetail',
    mixins: [BreadcrumbsManager],
    props: {
        facilityId: {
            type: String,
            required: true
        },
        qualityDefectId: {
            type: String,
            required: false,
            default: null
        }
    },
    mounted() {
        // init form
        if (this.isEdit) {
            this.initForm();
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Quality Defects'),
                route: {
                    name: 'quality_defect_list',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.isEdit
                    ? history.state.name
                    : this.$t('Create quality defect')
            }
        ]);
    },
    data() {
        return {
            form: {
                facility: this.facilityId,
                name: '',
                code: '',
                description: ''
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.qualityDefectId !== null;
        },
        /**
         * Payload ready for submission.
         * @typedef {Object} Payload
         * @property {Number} facility - Facility ID.
         * @property {String} name - Name.
         * @property {String} code - Code.
         * @property {String} description - Description.
         *
         * @returns {Payload} - The payload object.
         */
        payload() {
            return {
                facility: this.form.facility,
                name: this.form.name,
                code: this.form.code,
                description: this.form.description
            };
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form['name'] = history.state.name;
            this.form['code'] = history.state.code;
            this.form['description'] = history.state.description;
        },
        /**
         * Create quality defect.
         */
        async create() {
            // begin creating new quality defect
            this.beginLoading(`${this.$t('Creating new quality defect')}...`);

            try {
                await this.$axios.post('defects/', this.payload);
                this.$notifySuccess(this.$t('Quality defect created'));
                this.$router.go(-1);
            } catch (error) {
                this.handleApiErrors(error);
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Update quality defect.
         */
        async update() {
            // begin updating quality defect
            this.beginLoading(`${this.$t('Updating quality defect')}...`);
            try {
                await this.$axios.put(
                    `defects/${this.qualityDefectId}/`,
                    this.payload
                );
                this.$notifySuccess(this.$t('Quality defect updated'));
                this.$router.go(-1);
            } catch (error) {
                this.handleApiErrors(error);
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Handle remove quality defect.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this quality defect?')
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Remove quality defect.
         */
        async remove() {
            // begin deleting quality defect
            this.beginLoading(`${this.$t('Deleting quality defect')}...`);

            try {
                await this.$axios.delete(`defects/${this.qualityDefectId}`);
                this.$notifySuccess(this.$t('Quality defect deleted'));
                this.$router.go(-1);
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to remove quality defect. Please try again later.'
                    ),
                    error
                );
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Processes API error responses and triggers user notifications based on the field and error message.
         *
         * This function iterates over each error field in the response object. It evaluates the field name and
         * corresponding error message content to determine the appropriate user notification, utilizing `$notifyError`.
         *
         * Specific error handling includes:
         * - `error_code`: Checks for uniqueness constraint violations involving the facility and code fields.
         * - `code` and `name` fields: Validates against maximum character length constraints (100 characters).
         * - Other fields: Displays a generic error notification for unhandled errors.
         *
         * @param {Object} e - The error object returned from the API.
         */
        handleApiErrors(e) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            // Error message returned from the API
            const apiMessages = {
                unique: 'quality_defect_code_already_exists',
                maxLength: 'Ensure this field has no more than 100 characters.'
            };

            if (
                'error_code' in error &&
                Array.isArray(error['error_code']) &&
                error['error_code'].includes(apiMessages.unique)
            ) {
                this.$notifyError(
                    this.$t(
                        'A quality defect with the same code already exists in this facility.'
                    )
                );
            } else if (
                'name' in error &&
                error['name'][0] === apiMessages.maxLength
            ) {
                this.$notifyError(
                    this.$t(
                        'Ensure the quality defect name has no more than 100 characters.'
                    )
                );
            } else {
                this.$notifyError(
                    this.$t('Failed to create quality defect'),
                    error
                );
            }
        }
    }
};
</script>
